import React, { useRef, useState, useEffect } from 'react'
import { uuidv4 } from 'helpers/utils'
import { MenuProps } from 'components/menu'
import FloatingMenu from 'components/menu/floating'
import { MenuHeaderAction } from 'components/menu/items'
import FilterItem from './FilterItem'
import { ITableViewFilter, ITableColumn } from 'types'
import { IUIFilter } from 'components/spreadsheet/types'
import {
  buildSlicerRuntimeFilters,
  isUIFilterActive,
  getOriginalColumns
} from 'components/spreadsheet/helpers/functions'
import { useSlicers } from 'providers/slicers'
import {
  BLOCK_FILTER_BY,
  COLUMN_TYPE_NAMES,
  CommentDisplayTypes,
  ValidationDisplayTypes
} from 'components/spreadsheet/constants/const'
import { getViewCapabilities } from 'components/spreadsheet/constants/viewCapabilities'
import { useApplicationStore } from 'hooks/application'
import { cancelTimeout, requestTimeout, TimeoutID } from 'helpers/timer'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { Comment, ErrorOutline, Lock } from 'components/icons'
import { PanelAdd, PanelRules, PanelSection, PanelSelectRow } from 'components/menu/panel'
import IconSelector from 'components/spreadsheet/components/cell/components/icon'
import { getDefaultFilterTypeForColType } from 'components/spreadsheet/helpers/filtering'

const FilterMenu: React.FC<MenuProps> = ({ id, menuState, setMenuState, width }) => {
  const { spreadsheetData, setSpreadsheetData } = useDataContext()
  const { selections: slicerSelections } = useSlicers()

  // Filters imposed by document slicers — shown read-only so users know rows are constrained
  // beyond the view's own filters. They are applied at runtime and never saved to the view.
  const slicerFilters = buildSlicerRuntimeFilters(spreadsheetData.viewDetails.columns, slicerSelections)

  const globalFilters = spreadsheetData.userConfiguration.filterSettings.map((filter) => ({
    filter,
    cid: uuidv4()
  }))

  const [localFilters, setLocalFilters] = useState<IUIFilter[]>(
    menuState.defaultFilter ? [...globalFilters, { filter: menuState.defaultFilter, cid: uuidv4() }] : globalFilters
  )

  const timeoutRef = useRef<TimeoutID | null>(null)
  const { setSnackbarMessage } = useApplicationStore()

  // When there are changes to filters, propagate changes to main state
  useEffect(() => {
    const filterChange = localFilters !== globalFilters
    if (spreadsheetData.loading || !filterChange) {
      return
    }
    if (timeoutRef.current) {
      cancelTimeout(timeoutRef.current)
    }
    // The view it's made in: should the author have switched view by the time it saves, it's dropped.
    const viewId = spreadsheetData.viewDetails.publicId
    timeoutRef.current = requestTimeout(() => {
      // Convert UI filters to data filters, and remove any data filters that are empty
      const filters = localFilters.map((ui) => ui.filter).filter(isUIFilterActive)

      setSpreadsheetData({ type: 'UPDATE_FILTERS', filters, viewId })
    }, 1000)
  }, [localFilters, spreadsheetData.loading])

  const handleAddFilter = (columnId: string) => {
    const column = filterColumns.find((c) => c.publicId === columnId)
    if (column) {
      if (BLOCK_FILTER_BY.includes(column.kind)) {
        setSnackbarMessage({
          status: 'error',
          message: `${COLUMN_TYPE_NAMES[column.kind]} columns can't be used to filter a view.`
        })
        return
      }
      const newFilter: ITableViewFilter = {
        columnId: column.publicId,
        columnName: column.name,
        filterType: getDefaultFilterTypeForColType(column.kind),
        value: '',
        multipleValues: [],
        orGroup: 'main'
      }

      setLocalFilters([...localFilters, { cid: uuidv4(), filter: newFilter }])
    }
  }

  const handleFilterChange = (newFilter: IUIFilter) => {
    const newFilters = localFilters.map((filter) => {
      if (filter.cid == newFilter.cid) {
        return newFilter
      } else {
        return filter
      }
    })

    setLocalFilters(newFilters)
  }

  const handleDelete = (uiFilter: IUIFilter) => {
    const newFilters = localFilters.filter((f) => f.cid !== uiFilter.cid)
    setLocalFilters(newFilters)
  }

  const handleChangeGrouping = (changedUIFilter: IUIFilter, grouping: string) => {
    const newFilters = localFilters.map((uiFilter) => {
      if (changedUIFilter.cid === uiFilter.cid) {
        const groupId = grouping === 'and' ? 'main' : uuidv4()
        return {
          ...uiFilter,
          filter: {
            ...uiFilter.filter,
            orGroup: groupId
          }
        }
      } else {
        return uiFilter
      }
    })

    setLocalFilters(newFilters)
  }

  const handleValidationChange = (option: string) => {
    const newValue = parseInt(option)
    setSpreadsheetData({ type: 'CHANGE_DISPLAY_VALIDATION_ROWS', displayValidationErrorRows: newValue })
  }

  const handleCommentRowsChange = (option: string) => {
    const newValue = parseInt(option)
    setSpreadsheetData({ type: 'CHANGE_DISPLAY_COMMENT_ROWS', displayCommentRows: newValue })
  }

  const filterColumns = getOriginalColumns(spreadsheetData)
  const columnOptions = filterColumns
    .filter((column: ITableColumn) => !spreadsheetData.userConfiguration.hiddenColumns.includes(column.publicId))
    .filter((column) => !BLOCK_FILTER_BY.includes(column.kind))
    .map((column) => ({
      label: column.name,
      value: column.publicId,
      icon: <IconSelector kind={column.kind} />
    }))

  const allColumns = filterColumns

  // The metadata filters are part of what the view filters by, so Clear all clears them too.
  const metadataFiltered =
    spreadsheetData.viewDetails.displayValidationErrorRows !== ValidationDisplayTypes.SHOW_VALIDATION ||
    spreadsheetData.viewDetails.displayCommentRows !== CommentDisplayTypes.SHOW_ALL
  const clearAll = () => {
    setLocalFilters([])
    if (spreadsheetData.viewDetails.displayValidationErrorRows !== ValidationDisplayTypes.SHOW_VALIDATION) {
      handleValidationChange(String(ValidationDisplayTypes.SHOW_VALIDATION))
    }
    if (spreadsheetData.viewDetails.displayCommentRows !== CommentDisplayTypes.SHOW_ALL) {
      handleCommentRowsChange(String(CommentDisplayTypes.SHOW_ALL))
    }
  }

  const validationOptions = [
    { value: String(ValidationDisplayTypes.SHOW_VALIDATION), label: 'Show all rows' },
    { value: String(ValidationDisplayTypes.HIDE_VALIDATION), label: 'Hide rows with validation errors' },
    { value: String(ValidationDisplayTypes.ONLY_VALIDATION), label: 'Only show rows with validation errors' }
  ]
  const commentOptions = [
    { value: String(CommentDisplayTypes.SHOW_ALL), label: 'Show all rows' },
    { value: String(CommentDisplayTypes.SHOW_WITH_COMMENTS), label: 'Only show rows with comments' },
    { value: String(CommentDisplayTypes.SHOW_RESOLVED), label: 'Only show rows whose comments are all resolved' },
    { value: String(CommentDisplayTypes.SHOW_UNRESOLVED), label: 'Only show rows with unresolved comments' }
  ]

  return (
    <FloatingMenu
      id={id}
      menuState={menuState}
      setMenuState={setMenuState}
      width={width}
      zIndex={3000}
      title="Filter"
      action={
        (localFilters.length > 0 || metadataFiltered) && (
          <MenuHeaderAction onClick={clearAll}>Clear all</MenuHeaderAction>
        )
      }
    >
      {slicerFilters.length > 0 && (
        // Set by the document's slicers: shown so the rows they hide aren't a mystery, but changed there.
        <div data-testid="slicer-filters-section" className="mx-3 mb-2 flex flex-col gap-1">
          {slicerFilters.map((filter) => (
            <div
              key={filter.columnId}
              className="flex items-center gap-2 rounded-md bg-surface-sunken px-2 py-1.5 text-xs text-muted"
            >
              <Lock className="size-3.5 shrink-0" />
              <span className="min-w-0 truncate">
                <span className="text-body">{filter.columnName}</span> is one of {filter.multipleValues.join(', ')}
              </span>
              <span className="ml-auto shrink-0">from a slicer</span>
            </div>
          ))}
        </div>
      )}

      {localFilters.length > 0 && (
        <PanelRules ids={localFilters.map((uiFilter) => uiFilter.cid)}>
          {localFilters.map((uiFilter, idx) => (
            <FilterItem
              key={uiFilter.cid}
              position={idx}
              uiFilter={uiFilter}
              columns={allColumns}
              onChange={handleFilterChange}
              onDelete={handleDelete}
              onChangeGrouping={handleChangeGrouping}
              optionsOpen={menuState.defaultFilter !== undefined && idx === localFilters.length - 1}
            />
          ))}
        </PanelRules>
      )}

      <PanelAdd data-tour="add-filter" label="Add filter" options={columnOptions} onPick={handleAddFilter} />

      <PanelSection label="Metadata filters">
        <PanelSelectRow
          label="Validation errors"
          icon={<ErrorOutline />}
          options={validationOptions}
          selected={String(spreadsheetData.viewDetails.displayValidationErrorRows)}
          onPick={handleValidationChange}
          menuWidth={280}
        />
        {getViewCapabilities(spreadsheetData.viewDetails.type).toolbar.commentFilter && (
          <PanelSelectRow
            label="Comments"
            icon={<Comment />}
            options={commentOptions}
            selected={String(spreadsheetData.viewDetails.displayCommentRows)}
            onPick={handleCommentRowsChange}
            menuWidth={320}
          />
        )}
      </PanelSection>
    </FloatingMenu>
  )
}

export default FilterMenu
